// =============================================================================
// parameters-client.http.ts — Adaptateur HTTP RÉEL du ParametersClient (écran A-24, D4).
// =============================================================================
// Remplace le stub-first (Option B) derrière le MÊME DI seam (ParametersClientProvider) :
// les hooks/écrans consomment l'interface ParametersClient, l'impl est substituée sans
// toucher l'UI.
//
// AUTORITÉ DE VALIDATION = API ([S2-TRANCHÉ #4]) : validation valid_range (EF-PAR-1),
// journal + DE (EF-PAR-2). Cet adaptateur ne duplique AUCUNE règle : il sérialise la
// valeur, parse la réponse et relaie le message d'erreur serveur tel quel.
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par
// l'API (`credentials: 'include'`). CSRF DOUBLE-SUBMIT : le PATCH (mutation) ré-émet le
// cookie NON-httpOnly `admin_csrf` en en-tête `x-csrf-token` (AdminSessionGuard l'exige ;
// sans lui = 403). Les GET (list/changelog) ne portent PAS de CSRF. Aucune donnée métier
// en localStorage.

import {
  ParametersApiError,
  type CampaignActivityView,
  type ChangeLogView,
  type ParameterView,
  type ParametersClient,
  type UpdateParameterInput,
} from '@/api/parameters-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/** Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel du client A-24. `baseUrl` par défaut vide (chemins relatifs : SPA
 * et API partagent l'origine, ou proxy). En test, fetch est mocké.
 */
export class HttpParametersClient implements ParametersClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async list(): Promise<ParameterView[]> {
    return this.request<ParameterView[]>('/app-parameters', { method: 'GET' });
  }

  /** GET /app-parameters/campaign-activity — signal du bandeau d'impact (V28-IMPACT-BANNER). */
  public async getCampaignActivity(): Promise<CampaignActivityView> {
    return this.request<CampaignActivityView>('/app-parameters/campaign-activity', {
      method: 'GET',
    });
  }

  public async changelog(paramKey: string): Promise<ChangeLogView[]> {
    return this.request<ChangeLogView[]>(
      `/app-parameters/${encodeURIComponent(paramKey)}/changelog`,
      { method: 'GET' },
    );
  }

  public async update(
    paramKey: string,
    input: UpdateParameterInput,
  ): Promise<ParameterView> {
    return this.request<ParameterView>(
      `/app-parameters/${encodeURIComponent(paramKey)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
  }

  /**
   * Exécute une requête same-origin (cookie httpOnly), parse le JSON typé, ou lève une
   * ParametersApiError. Sur mutation (PATCH), ré-émet le cookie `admin_csrf` en en-tête
   * `x-csrf-token` (double-submit) ; les GET n'en portent pas.
   */
  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse d'erreur en ParametersApiError (message serveur, sinon repli). */
  private async toApiError(response: Response): Promise<ParametersApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur les paramètres applicatifs.`;
    return new ParametersApiError(message, response.status);
  }
}
